import fs from 'fs/promises';
import fssync from 'fs';
import path from 'path';
import { runRipgrep } from '../utils/ripgrep.js';
import { ensureWithinRepo, resolveUnderRepo, toRepoRelative, isSymlink, isTextualFile, fixPathRepetition } from '../utils/paths.js';
import type { WarpGrepProvider, GrepResult, ReadResult, GlobResult, ListDirectoryEntry } from './types.js';
import { readAllLines } from '../utils/files.js';
import { DEFAULT_EXCLUDES, AGENT_CONFIG } from '../agent/config.js';

/**
 * Directories/files to always skip (exact name match)
 */
const SKIP_NAMES = new Set([
  // Version control
  '.git', '.svn', '.hg', '.bzr',
  // Dependencies
  'node_modules', 'bower_components', '.pnpm', '.yarn',
  'vendor', 'Pods', '.bundle',
  // Python
  '__pycache__', '.pytest_cache', '.mypy_cache', '.ruff_cache',
  '.venv', 'venv', '.tox', '.nox', '.eggs',
  // Build outputs
  'dist', 'build', 'out', 'output', 'target', '_build',
  '.next', '.nuxt', '.output', '.vercel', '.netlify',
  // Cache
  '.cache', '.parcel-cache', '.turbo', '.nx', '.gradle',
  // IDE
  '.idea', '.vscode', '.vs',
  // Coverage
  'coverage', '.coverage', 'htmlcov', '.nyc_output',
  // Temp
  'tmp', 'temp', '.tmp', '.temp',
  // Lock files
  'package-lock.json', 'yarn.lock', 'pnpm-lock.yaml', 'bun.lockb',
  'Cargo.lock', 'Gemfile.lock', 'poetry.lock',
]);

/**
 * File extensions to skip
 */

const SKIP_EXTENSIONS = new Set([
  '.min.js', '.min.css', '.bundle.js',
  '.wasm', '.so', '.dll', '.pyc',
  '.map', '.js.map',
]);

/**
 * Check if a filename should be skipped
 */
function shouldSkip(name: string, allowNames?: Set<string>): boolean {
  // Allow explicitly permitted names through
  if (allowNames?.has(name)) return false;

  // Skip exact name matches
  if (SKIP_NAMES.has(name)) return true;

  // Skip hidden files/directories (start with .)
  if (name.startsWith('.')) return true;

  // Skip files with certain extensions
  for (const ext of SKIP_EXTENSIONS) {
    if (name.endsWith(ext)) return true;
  }

  return false;
}

export class LocalRipgrepProvider implements WarpGrepProvider {
  private readonly allowNames?: Set<string>;

  constructor(
    private readonly repoRoot: string,
    private readonly excludes: string[] = DEFAULT_EXCLUDES,
    options?: { allowNames?: string[] },
  ) {
    if (options?.allowNames?.length) {
      this.allowNames = new Set(options.allowNames);
    }
  }

  async grep(params: { pattern: string; path: string; glob?: string; context_lines?: number; case_sensitive?: boolean }): Promise<GrepResult> {
    let abs: string;
    try {
      abs = resolveUnderRepo(this.repoRoot, params.path);
    } catch (err) {
      return {
        lines: [],
        error: `[PATH ERROR] ${err instanceof Error ? err.message : String(err)}`,
      };
    }
    const stat = await fs.stat(abs).catch(() => null);
    if (!stat) return { lines: [] };
    const targetArg = abs === path.resolve(this.repoRoot) ? '.' : toRepoRelative(this.repoRoot, abs);
    const contextLines = params.context_lines !== undefined ? String(params.context_lines) : '1';
    const args = [
      '--no-config',
      '--no-heading',
      '--with-filename',
      '--line-number',
      '--color=never',
      '--trim',
      '--max-columns=400',
      '-C', contextLines,
      ...(params.case_sensitive === false ? ['--ignore-case'] : []),
      ...(params.glob ? ['--glob', params.glob] : []),
      ...this.excludes.filter(e => !this.allowNames?.has(e)).flatMap((e) => ['-g', `!${e}`]),
      params.pattern,
      targetArg || '.',
    ];
    const res = await runRipgrep(args, { cwd: this.repoRoot });
    
    // Gracefully handle ripgrep not being available
    if (res.exitCode === -1) {
      return {
        lines: [],
        error: `[RIPGREP NOT AVAILABLE] ripgrep (rg) is required but failed to execute. Please install it:\n` +
               `  • macOS: brew install ripgrep\n` +
               `  • Ubuntu/Debian: apt install ripgrep\n` +
               `  • Windows: choco install ripgrep\n` +
               `  • Or visit: https://github.com/BurntSushi/ripgrep#installation\n` +
               `Exit code: ${res.exitCode}${res.stderr ? `\nDetails: ${res.stderr}` : ''}`,
      };
    }
    
    // Handle other ripgrep errors gracefully
    if (res.exitCode !== 0 && res.exitCode !== 1) {
      return {
        lines: [],
        error: `[RIPGREP ERROR] grep failed with exit code ${res.exitCode}${res.stderr ? `: ${res.stderr}` : ''}`,
      };
    }
    
    const lines = (res.stdout || '')
      .trim()
      .split(/\r?\n/)
      .filter((l) => l.length > 0);
    if (lines.length > AGENT_CONFIG.MAX_OUTPUT_LINES) {
      const truncated = lines.slice(0, AGENT_CONFIG.MAX_OUTPUT_LINES);
      truncated.push(`... (output truncated at ${AGENT_CONFIG.MAX_OUTPUT_LINES} of ${lines.length} lines)`);
      return { lines: truncated };
    }
    
    return { lines };
  }

  async read(params: { path: string; start?: number; end?: number }): Promise<ReadResult> {
    let abs: string;
    try {
      abs = resolveUnderRepo(this.repoRoot, params.path);
    } catch (err) {
      return {
        lines: [],
        error: `[PATH ERROR] ${err instanceof Error ? err.message : String(err)}`,
      };
    }
    let stat = await fs.stat(abs).catch(() => null);

    // Handle duplicate path segments from model predictions
    if (!stat || !stat.isFile()) {
      const fixedPath = fixPathRepetition(abs);
      if (fixedPath) {
        const fixedStat = await fs.stat(fixedPath).catch(() => null);
        if (fixedStat?.isFile()) {
          abs = fixedPath;
          stat = fixedStat;
        }
      }
    }

    // Gracefully handle file not found / not a file
    if (!stat || !stat.isFile()) {
      return {
        lines: [],
        error: `[FILE NOT FOUND] You tried to read "${params.path}" but there is no file at this path. ` +
               `Double-check the path exists and is spelled correctly.`,
      };
    }
    
    // Gracefully handle symlinks
    if (isSymlink(abs)) {
      return {
        lines: [],
        error: `[SYMLINK] You tried to read "${params.path}" but this is a symlink. ` +
               `Try reading the actual file it points to instead.`,
      };
    }
    
    // Gracefully handle non-text or too-large files
    if (!isTextualFile(abs)) {
      return {
        lines: [],
        error: `[UNREADABLE FILE] You tried to read "${params.path}" but this file is either too large ` +
               `or not a text file, so it cannot be read. Try a different file.`,
      };
    }
    
    let lines: string[];
    try {
      lines = await readAllLines(abs);
    } catch (err) {
      return {
        lines: [],
        error: `[READ ERROR] Failed to read "${params.path}": ${err instanceof Error ? err.message : String(err)}`,
      };
    }
    const total = lines.length;
    const rawStart = params.start;
    const rawEnd = params.end;
    
    let s = 1;
    let e = total;
    
    const startValid = rawStart === undefined || (Number.isFinite(rawStart) && rawStart > 0);
    const endValid = rawEnd === undefined || (Number.isFinite(rawEnd) && rawEnd > 0);
    
    if (startValid && endValid) {
      s = rawStart ?? 1;
      e = Math.min(rawEnd ?? total, total);
      if ((s > total && total > 0) || (s > e)) {
        s = 1;
        e = total;
      }
    }
    const out: string[] = [];
    for (let i = s; i <= e; i += 1) {
      const content = lines[i - 1] ?? '';
      out.push(`${i}|${content}`);
    }
    if (out.length > AGENT_CONFIG.MAX_READ_LINES) {
      const truncated = out.slice(0, AGENT_CONFIG.MAX_READ_LINES);
      truncated.push(`... (output truncated at ${AGENT_CONFIG.MAX_READ_LINES} of ${out.length} lines)`);
      return { lines: truncated };
    }
    
    return { lines: out };
  }

  async listDirectory(params: { path: string; pattern?: string | null; maxResults?: number; maxDepth?: number }): Promise<ListDirectoryEntry[]> {
    let abs: string;
    try {
      abs = resolveUnderRepo(this.repoRoot, params.path);
    } catch {
      // Path outside repo - return empty (graceful failure)
      return [];
    }
    const stat = await fs.stat(abs).catch(() => null);
    if (!stat || !stat.isDirectory()) {
      return [];
    }
    const maxResults = params.maxResults ?? AGENT_CONFIG.MAX_OUTPUT_LINES;
    const maxDepth = params.maxDepth ?? AGENT_CONFIG.MAX_LIST_DEPTH;
    const regex = params.pattern ? new RegExp(params.pattern) : null;
    const repoRoot = this.repoRoot;
    const allowNames = this.allowNames;

    const results: ListDirectoryEntry[] = [];
    let timedOut = false;
    const startTime = Date.now();

    async function walk(dir: string, depth: number) {
      if (Date.now() - startTime > AGENT_CONFIG.LIST_TIMEOUT_MS) {
        timedOut = true;
        return;
      }
      if (depth > maxDepth || results.length >= maxResults) return;
      let entries: import('fs').Dirent[];
      try {
        entries = await fs.readdir(dir, { withFileTypes: true });
      } catch {
        return; // skip unreadable directories (ENOENT, EACCES, etc.)
      }
      for (const entry of entries) {
        if (timedOut || results.length >= maxResults) break;

        // Simple name-based filtering - skip junk dirs/files
        if (shouldSkip(entry.name, allowNames)) continue;
        
        // Apply user-provided pattern filter
        if (regex && !regex.test(entry.name)) continue;
        
        const full = path.join(dir, entry.name);
        const isDir = entry.isDirectory();
        
        results.push({
          name: entry.name,
          path: toRepoRelative(repoRoot, full),
          type: isDir ? 'dir' : 'file',
          depth,
        });
        if (isDir) {
          await walk(full, depth + 1);
        }
      }
    }
    await walk(abs, 0);
    return results;
  }

  async glob(params: { pattern: string; path?: string }): Promise<GlobResult> {
    let abs: string;
    try {
      abs = params.path ? resolveUnderRepo(this.repoRoot, params.path) : this.repoRoot;
    } catch (err) {
      return { files: [], searchDir: this.repoRoot, totalFound: 0, error: `[PATH ERROR] ${err instanceof Error ? err.message : String(err)}` };
    }
    const stat = await fs.stat(abs).catch(() => null);
    if (!stat || !stat.isDirectory()) {
      return { files: [], searchDir: abs, totalFound: 0, error: `[PATH ERROR] Directory not found: ${params.path || '.'}` };
    }

    const targetArg = abs === path.resolve(this.repoRoot) ? '.' : toRepoRelative(this.repoRoot, abs);
    const args = [
      '--no-config',
      '--files',
      '--color=never',
      '-g', params.pattern,
      ...this.excludes.filter(e => !this.allowNames?.has(e)).flatMap(e => ['-g', `!${e}`]),
      targetArg || '.',
    ];

    const res = await runRipgrep(args, { cwd: this.repoRoot });
    if (res.exitCode === -1) {
      return { files: [], searchDir: abs, totalFound: 0, error: `[RIPGREP NOT AVAILABLE] ripgrep (rg) is required for glob search.` };
    }
    if (res.exitCode !== 0 && res.exitCode !== 1) {
      return { files: [], searchDir: abs, totalFound: 0, error: `[GLOB ERROR] glob failed with exit code ${res.exitCode}${res.stderr ? `: ${res.stderr}` : ''}` };
    }

    // Convert to absolute paths
    const absRoot = path.resolve(this.repoRoot);
    const relFiles = (res.stdout || '').trim().split(/\r?\n/).filter(l => l.length > 0);
    const absFiles = relFiles.map(f => path.resolve(absRoot, f));

    // Sort by mtime descending (newest first), matching training format
    const withMtime: Array<{ file: string; mtime: number }> = [];
    for (const f of absFiles) {
      const s = await fs.stat(f).catch(() => null);
      withMtime.push({ file: f, mtime: s?.mtimeMs ?? 0 });
    }
    withMtime.sort((a, b) => b.mtime - a.mtime);

    const totalFound = withMtime.length;
    return { files: withMtime.slice(0, 100).map(f => f.file), searchDir: abs, totalFound };
  }
}
